import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import WaveCanvas from '../WaveCanvas';

const navItems = [
  {
    label: "Who we are",
    link: "/who-we-are",
  },
  {
    label: "What we do",
    link: "/what-we-do",
    showImage: true,
    children: [
      { label: "Agentic AI Services for Enterprises", link: "/agentic-ai-services" },
      { label: "AI & Automation", link: "/ai-automation" },
      { label: "Product Engineering", link: "/product-engineering" },
      { label: "Performance Engineering", link: "/performance-engineering" },
      { label: "Cloud Engineering", link: "/cloud-engineering" },
      { label: "divider" },
      { label: "ServiceNow Consulting", link: "/servicenow-consulting" },
      { label: "Data & Analytics", link: "/data-analytics" },
      { label: "Infor M3 Solutions", link: "/infor-m3-solutions" },
      { label: "Infor EPM Services", link: "/infor-epm-services" },
      { label: "Global Capability Centers", link: "/global-capability-centers" },
      { label: "divider" },
      { label: "MES Solutions", link: "/manufacturing-execution-system" },
      {
        label: "Industrial Automation",
        link: "/industrial-automation",
        subItems: [
          { label: "Data Extraction & Analytics", link: "/data-extraction-analytics" },
        ]
      },
      { label: "Embedded Services", link: "/embedded-services" },
      { label: "Staff Augmentation", link: "/staff-augmentation" },
    ],
  },
  {
    label: "Capabilities",
    link: "/capabilities",
    isDarkMegaMenu: true,
    sections: [
      {
        title: "Capabilities",
        col1: [
          { label: "AI Automation", link: "/capabilities/ai-automation" },
          { label: "Enterprise Applications", link: "/capabilities/enterprise-applications" },
          { label: "Digital Workflows", link: "/capabilities/digital-workflows" },
          { label: "Cloud Engineering", link: "/capabilities/cloud-engineering" },
          { label: "Industrial Engineering", link: "/capabilities/industrial-engineering" },
          { label: "Product Engineering", link: "/capabilities/product-engineering" },
          { label: "Quality Engineering", link: "/capabilities/quality-engineering" },
          { label: "Managed Services", link: "/capabilities/managed-services" },
        ],
        col2: []
      },
      {
        title: "Industries",
        col1: [
          { label: "Retail & E-Commerce", link: "/industries/retail" },
          { label: "Healthcare & Life Sciences", link: "/industries/healthcare" },
          { label: "Energy, Oil & Gas", link: "/industries/energy" },
          { label: "Telecommunications", link: "/industries/telecom" },
          { label: "Media & Entertainment", link: "/industries/media" },
          { label: "Transportation & Logistics", link: "/industries/logistics" },
          { label: "Travel & Hospitality", link: "/industries/travel" },
          { label: "Government & Public Sector", link: "/industries/government" },
          { label: "Education (EdTech)", link: "/industries/education" },
          { label: "Real Estate & Construction", link: "/industries/real-estate" },
        ],
        col2: [
          { label: "Agriculture & AgriTech", link: "/industries/agriculture" },
          { label: "Consumer Goods (CPG/FMCG)", link: "/industries/cpg" },
          { label: "Professional Services", link: "/industries/professional-services" },
          { label: "Technology & Software", link: "/industries/tech-software" },
          { label: "Automotive & Mobility", link: "/industries/automotive" },
          { label: "Aerospace & Defense", link: "/industries/aerospace" },
          { label: "Utilities", link: "/industries/utilities" },
          { label: "Non-Profit & NGOs", link: "/industries/non-profit" },
          { label: "Banking, Financial Services & Insurance", link: "/industries/bfsi" },
          { label: "Manufacturing & Industrial Automation", link: "/industries/manufacturing" },
        ]
      }
    ]
  },
  {
    label: "Products",
    link: "",
    showImage: true,
    children: [
      {
        label: "Glodesk",
        link: "/glodesk",
        image: "/assets/products/glodesk-thumb.png",
        description: "Transform your IT support with Glodesk, an advanced AI-powered service desk solution for modern enterprises."
      },
      {
        label: "Glokode",
        link: "/glokode",
        image: "/assets/products/glokode-thumb.png",
        description: "Accelerate your development cycle with Glokode, a revolutionary low-code platform to build apps faster."
      },
    ],
  },
  {
    label: "Careers",
    link: "/careers",
    isCareerDirectMenu: true,
    children: [
      { label: "Working Here", link: "/working-here" },
      { label: "Work-Environment", link: "/work-environment" },
      { label: "About recruiting at Mobikode", link: "/recruiting" },
      { label: "Jobs at Mobikode", link: "/all-openings" },
    ]
  },
  { label: "Contact", link: "/contact" },
];

function DemoHeader() {
  const [mobileMenu, setMobileMenu] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [activeMobileSub, setActiveMobileSub] = useState(null);
  const [nestedMobileSub, setNestedMobileSub] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [hoveredSubItem, setHoveredSubItem] = useState(false);

  const location = useLocation();
  const isNavbarActive = scrolled || openDropdown !== null;

  useEffect(() => {
    setMobileMenu(false);
    setOpenDropdown(null);
    setActiveMobileSub(null);
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setMobileMenu(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleMobileSub = (label) => {
    setActiveMobileSub(activeMobileSub === label ? null : label);
  };

  return (
    <>
      <div
        className={`fixed top-0 w-full z-50 transition-all duration-500 ease-in-out group
          ${isNavbarActive ? 'py-[10px] bg-white backdrop-blur-md shadow-lg' : 'py-[10px] bg-transparent shadow-none'}
        `}
      >
        <div className="mx-auto w-full max-w-[1220px] flex justify-between items-center lg:px-[80px] px-[20px]">

          <div className="flex items-center cursor-pointer overflow-hidden relative h-[80px] group w-[250px]">
            <div className="grid grid-cols-1 grid-rows-1 items-center w-full h-full">
              <Link to="/" className={`col-start-1 row-start-1 z-10 block w-auto transition-all duration-500 ease-in-out ${!isNavbarActive ? 'opacity-0 -translate-y-full' : 'opacity-100 translate-y-0 group-hover:opacity-0 group-hover:-translate-y-full'}`}>
                <img src="/assets/nav/logo.svg" alt="icon" style={{ maxHeight: '40px' }} />
              </Link>
              <Link to="/" className={`col-start-1 row-start-1 z-20 block w-auto transition-all duration-500 ease-in-out ${!isNavbarActive ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-full group-hover:opacity-100 group-hover:translate-y-0'}`}>
                <img src="/assets/nav/newlogo.png" className={`w-auto transition-all duration-300 lg:max-h-[40px] max-h-[33px] ${!isNavbarActive ? 'brightness-0 invert' : 'brightness-0'}`} alt="logo" />
              </Link>
            </div>
          </div>

          <div className="hidden lg:flex items-center gap-6">
            <ul className="flex font-sora space-x-2 gap-[20px] text-lg font-medium relative">
              {navItems.map((item, index) => (
                <li
                  key={index}
                  className="relative"
                  onMouseEnter={() => (item.children || item.sections) && setOpenDropdown(index)}
                  onMouseLeave={() => (item.children || item.sections) && setOpenDropdown(null)}
                >
                  <Link
                    to={item.link}
                    className={`relative inline-flex items-center font-graphik gap-1 py-[26px] text-[16px] font-[600] cursor-pointer group/menuitem transition-colors duration-300 ${isNavbarActive ? 'text-black' : 'text-white'}`}
                  >
                    <span className="relative">
                      {item.label}
                      <span className={`absolute left-0 bottom-[-8px] h-[2px] transition-all duration-300 ${isNavbarActive ? 'bg-black' : 'bg-white'} ${openDropdown === index ? 'w-full' : 'w-0 group-hover/menuitem:w-full'}`}></span>
                    </span>
                    {(item.children || item.sections) && (
                      <svg className={`w-5 h-5 ml-1 transition-transform duration-200 ${openDropdown === index ? 'rotate-180' : 'rotate-0'}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    )}
                  </Link>

                  <AnimatePresence>
                    {(item.children || item.sections) && openDropdown === index && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className={`${item.label === "Who we are"
                          ? "absolute top-[80px] left-1/2 -translate-x-1/2 w-[220px]"
                          : "fixed top-[80px] left-0 w-full"
                          } z-50`}
                      >
                        {item.isDarkMegaMenu ? (
                          <div className="bg-[#FFFFFF] relative w-full flex flex-col shadow-2xl border-t border-gray-100 overflow-hidden">
                            <div className="absolute top-[20%] -left-[10%] w-[120%] h-[120%] z-0 pointer-events-none opacity-30 -rotate-[12deg] scale-125 origin-center">
                              <WaveCanvas />
                            </div>
                            <div className="relative z-10 max-w-[1440px] w-full mx-auto lg:px-[80px] px-[20px] pt-[40px] pb-[60px]">
                              <div className="grid grid-cols-3 w-full">
                                <div className="flex flex-col h-full border-r border-gray-200 pr-[40px]">
                                  <h3 className="text-[24px] font-sora font-[600] text-[#01386A] tracking-tight mb-6">
                                    {item.sections[0].title}
                                  </h3>
                                  <div className="flex flex-col gap-y-4">
                                    {item.sections[0].col1.map((link, i) => (
                                      <Link key={i} to={link.link} className="text-[#6C7689] text-[14px] font-[600] hover:text-black transition-colors" onClick={() => setOpenDropdown(null)}>
                                        {link.label}
                                      </Link>
                                    ))}
                                  </div>
                                </div>
                                <div className="flex flex-col h-full px-[40px]">
                                  <h3 className="text-[24px] font-sora font-[600] text-[#01386A] tracking-tight mb-6">
                                    {item.sections[1].title}
                                  </h3>
                                  <div className="flex flex-col gap-y-4">
                                    {item.sections[1].col1.map((link, i) => (
                                      <Link key={i} to={link.link} className="text-[#6C7689] text-[14px] font-[600] hover:text-black transition-colors" onClick={() => setOpenDropdown(null)}>
                                        {link.label}
                                      </Link>
                                    ))}
                                  </div>
                                </div>
                                <div className="flex flex-col h-full pl-[40px]">
                                  <h3 className="text-[24px] font-sora font-[600] text-transparent select-none pointer-events-none tracking-tight mb-6">
                                    {item.sections[1].title}
                                  </h3>
                                  <div className="flex flex-col gap-y-4">
                                    {item.sections[1].col2.map((link, i) => (
                                      <Link key={i} to={link.link} className="text-[#6C7689] text-[14px] font-[600] hover:text-black transition-colors" onClick={() => setOpenDropdown(null)}>
                                        {link.label}
                                      </Link>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-white shadow-lg w-full flex flex-wrap border-t border-gray-100">
                            <div className={`${item.label === "Who we are"
                              ? "p-2"
                              : "max-w-[1440px] w-full mx-auto lg:px-[80px] px-[20px] flex items-start justify-between gap-[50px] pt-[26px] pb-[50px]"
                              }`}>

                              <div className="flex-grow">
                                {item.label !== "Who we are" && (
                                  <h6 className="text-[24px] font-sora font-[600] text-[#01386A] pb-[25px] text-left">
                                    {item.label}
                                  </h6>
                                )}

                                {item.label === "Products" ? (
                                  <div className="grid grid-cols-2 pl-[20px] border-l border-[#6C76894A] w-full gap-x-[30px] text-left">
                                    {item.children.map((child, i) => (
                                      <div key={i} className={i === 1 ? "pl-[20px] border-l border-[#6C76894A]" : ""}>
                                        <h6 className="font-graphik font-[600] text-[18px] text-[#6C7689] pb-2">{child.label}</h6>
                                        <p className="text-[14px] text-[#6C7689] leading-relaxed mb-3">{child.description}</p>
                                        <Link to={child.link} onClick={() => setOpenDropdown(null)} className="text-[12px] underline flex items-center gap-1 text-[#6C7689] hover:text-black transition-colors">Learn More <ChevronRight size={14} /></Link>
                                      </div>
                                    ))}
                                  </div>
                                ) : item.isCareerDirectMenu ? (
                                  <div className="grid grid-cols-4 gap-y-6 w-full text-left items-stretch">
                                    {item.children.map((child, i) => (
                                      <div key={i} className="flex flex-col justify-start border-l border-[#6C76894A] pl-[25px] py-1">
                                        <Link to={child.link} onClick={() => setOpenDropdown(null)} className="font-sora text-[15px] font-[600] text-[#6C7689] hover:text-[#01386A] transition-colors leading-normal">
                                          {child.label}
                                        </Link>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="grid grid-cols-3 pl-[20px] border-l border-[#6C76894A] w-full gap-x-[30px] text-left">
                                    <div className="flex flex-col gap-y-[20px]">
                                      {item.children.slice(0, 5).map((child, i) => (
                                        child.label === "divider" ? "" :
                                          <Link key={i} to={child.link} onClick={() => setOpenDropdown(null)} className="block whitespace-nowrap font-graphik font-[600] text-[14px] text-[#6C7689] hover:text-black">{child.label}</Link>
                                      ))}
                                    </div>
                                    <div className="flex flex-col gap-y-[20px] pl-[20px] border-l border-[#6C76894A]">
                                      {item.children.slice(5, 11).map((child, i) => (
                                        child.label === "divider" ? "" :
                                          <Link key={i} to={child.link} onClick={() => setOpenDropdown(null)} className="block whitespace-nowrap font-graphik font-[600] text-[14px] text-[#6C7689] hover:text-black">{child.label}</Link>
                                      ))}
                                    </div>
                                    <div className="flex flex-col gap-y-[20px] pl-[20px] border-l border-[#6C76894A]">
                                      {item.children.slice(11, 17).map((child, i) => (
                                        child.label === "divider" ? "" :
                                          <div
                                            key={i}
                                            className="relative flex flex-col items-start w-full"
                                            onMouseEnter={() => child.subItems && setHoveredSubItem(true)}
                                            onMouseLeave={() => child.subItems && setHoveredSubItem(false)}
                                          >
                                            <div className="inline-flex items-center gap-2 whitespace-nowrap font-graphik font-[600] text-[14px] text-[#6C7689] hover:text-black w-full justify-between pr-4 cursor-pointer">
                                              <Link to={child.link} onClick={() => setOpenDropdown(null)}>
                                                {child.label}
                                              </Link>
                                              {child.subItems && <ChevronDown size={14} className={`transition-transform duration-300 ${hoveredSubItem ? 'rotate-180' : ''}`} />}
                                            </div>

                                            <AnimatePresence>
                                              {child.subItems && hoveredSubItem && (
                                                <motion.div
                                                  initial={{ opacity: 0, height: 0, marginTop: 0 }}
                                                  animate={{ opacity: 1, height: "56px", marginTop: 8 }}
                                                  exit={{ opacity: 0, height: 0, marginTop: 0 }}
                                                  transition={{ duration: 0.3, ease: "easeInOut" }}
                                                  className="flex flex-col w-full bg-[#01386A] p-2 gap-1 overflow-hidden"
                                                >
                                                  {child.subItems.map((sub, subIdx) => (
                                                    <Link
                                                      key={subIdx}
                                                      to={sub.link}
                                                      className="px-3 py-1.5 text-[13px] font-[600] text-[#FFFFFF] transition-colors block text-left"
                                                      onClick={() => setOpenDropdown(null)}
                                                    >
                                                      {sub.label}
                                                    </Link>
                                                  ))}
                                                </motion.div>
                                              )}
                                            </AnimatePresence>
                                          </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>

                              {(item.label === "What we do") && (
                                <div className="max-w-[396px] w-full hidden md:block">
                                  <div className="h-[265px] w-full overflow-hidden shadow-md rounded-sm">
                                    <img
                                      src="/assets/what/navbar-img.png"
                                      className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                                      alt="navigation visuals"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </li>
              ))}
            </ul>
          </div>

          <button className="lg:hidden flex flex-col gap-1.5 p-2" onClick={() => setMobileMenu(true)}>
            <span className={`w-6 h-0.5 transition-colors ${isNavbarActive ? 'bg-black' : 'bg-white'}`}></span>
            <span className={`w-6 h-0.5 transition-colors ${isNavbarActive ? 'bg-black' : 'bg-white'}`}></span>
            <span className={`w-4 h-0.5 transition-colors self-end ${isNavbarActive ? 'bg-black' : 'bg-white'}`}></span>
          </button>
        </div>
      </div>

      {mobileMenu && (
        <div className="fixed inset-0 bg-black/50 z-[99998] lg:hidden" onClick={() => setMobileMenu(false)}></div>
      )}

      <div className={`fixed top-0 right-0 h-full w-[85%] max-w-[400px] z-[99999] bg-white shadow-2xl transform transition-transform duration-300 ease-in-out overflow-y-auto lg:hidden ${mobileMenu ? "translate-x-0" : "translate-x-full"}`}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-10">
            <img src="/assets/nav/newlogo.png" className="h-[30px] brightness-0" alt="logo" />
            <button onClick={() => setMobileMenu(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              <X size={28} className="text-black" />
            </button>
          </div>

          <nav className="flex flex-col gap-2">
            {navItems.map((item, idx) => (
              <div key={idx} className="border-b border-gray-100 last:border-0">
                {(item.children || item.sections) ? (
                  <>
                    <button
                      onClick={() => toggleMobileSub(item.label)}
                      className="flex justify-between items-center w-full py-4 text-[18px] font-semibold text-[#01386A] text-left"
                    >
                      {item.label}
                      <ChevronDown size={20} className={`transition-transform duration-300 ${activeMobileSub === item.label ? "rotate-180" : ""}`} />
                    </button>

                    <div className={`overflow-hidden transition-all duration-300 ${activeMobileSub === item.label ? "max-h-[3000px] pb-4" : "max-h-0"}`}>
                      <div className="flex flex-col gap-3 pl-4 border-l-2 border-gray-100 text-left">
                        {item.isDarkMegaMenu ? (
                          item.sections.map((sec, secIdx) => (
                            <React.Fragment key={secIdx}>
                              <div className="text-[#01386A] font-bold text-[14px] mt-2 mb-1">{sec.title}</div>
                              {[...sec.col1, ...sec.col2].map((child, cIdx) => (
                                <Link key={cIdx} to={child.link} className="text-[15px] text-gray-600 hover:text-[#01386A] py-1 pl-2" onClick={() => setMobileMenu(false)}>
                                  {child.label}
                                </Link>
                              ))}
                            </React.Fragment>
                          ))
                        ) : (
                          item.children.map((child, cIdx) => (
                            child.label === "divider" ? (
                              <div key={cIdx} className="my-2 border-t border-gray-50"></div>
                            ) : (
                              <div key={cIdx}>
                                <div
                                  className="flex justify-between items-center py-1 cursor-pointer"
                                  onClick={() => child.subItems && setNestedMobileSub(nestedMobileSub === child.label ? null : child.label)}
                                >
                                  <Link to={child.link} className="text-[15px] text-gray-600 hover:text-[#01386A]" onClick={() => !child.subItems && setMobileMenu(false)}>
                                    {child.label}
                                  </Link>
                                  {child.subItems && <ChevronDown size={16} className={nestedMobileSub === child.label ? "rotate-180" : ""} />}
                                </div>
                                {child.subItems && nestedMobileSub === child.label && (
                                  <div className="bg-[#F4F7FF] ml-2 mt-1 rounded-md p-2 flex flex-col gap-2">
                                    {child.subItems.map((sub, sIdx) => (
                                      <Link key={sIdx} to={sub.link} className="text-[14px] text-[#01386A] py-1 pl-2 border-l border-blue-200" onClick={() => setMobileMenu(false)}>
                                        {sub.label}
                                      </Link>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )
                          ))
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <Link to={item.link} onClick={() => setMobileMenu(false)} className="block py-4 text-[18px] font-semibold text-[#01386A] text-left">
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          <div className="mt-10">
            <Link to="/contact" onClick={() => setMobileMenu(false)} className="block w-full text-center bg-[#0E2A62] text-white py-4 rounded-lg font-bold hover:bg-[#01386A] transition-colors">
              Get Started
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}

export default DemoHeader;